import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, ArrowRight, Banknote, CreditCard, FileText, RefreshCcw, ChevronLeft, ChevronRight, Search, Filter } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

const PAGE_SIZE = 50;

export default function RiconciliazioniPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filterTipo, setFilterTipo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (filterTipo && filterTipo !== "all") params.append("tipo", filterTipo);
      if (searchText) params.append("search", searchText);
      
      const res = await api.get(`${getApiPrefix()}/riconciliazioni?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      handleApiError(err, t("errors.loadReconciliations"));
      
    } finally {
      setLoading(false);
    }
  }, [page, filterTipo, searchText, t]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(`${getApiPrefix()}/riconciliazioni/stats`);
      setStats(res.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch riconciliazioni stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { setPage(1); }, [filterTipo, searchText]);

  const handleSearch = () => {
    setSearchText(searchInput);
  };

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const getTipoBadge = (tipo, origineTipo) => {
    if (tipo === 'fattura') {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{origineTipo === 'banca' ? 'Banca→Fattura' : 'Carta→Fattura'}</Badge>;
    }
    if (tipo === 'giroconto_banca') {
      return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">{t('common.transferBank')}</Badge>;
    }
    if (tipo === 'giroconto_carta') {
      return <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">{t('common.transferCard')}</Badge>;
    }
    if (tipo === 'conto') {
      return <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">{origineTipo === 'banca' ? 'Banca→Conto' : 'Carta→Conto'}</Badge>;
    }
    return <Badge variant="outline">{t('common.other')}</Badge>;
  };

  const getOriginIcon = (tipo) => {
    if (tipo === 'banca') return <Banknote className="h-4 w-4 text-emerald-600" />;
    return <CreditCard className="h-4 w-4 text-violet-600" />;
  };

  return (
    <div className="space-y-6" data-testid="riconciliazioni-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("reconciliations.title")}</h1>
          <p className="text-muted-foreground">Tutti i collegamenti tra movimenti bancari, carte e fatture</p>
        </div>
        <Button variant="outline" onClick={() => { fetchItems(); fetchStats(); }}>
          <RefreshCcw className="h-4 w-4 mr-2" />{t('common.refresh')}</Button>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={`skeleton-${i}`} className="animate-pulse">
              <CardContent className="pt-6"><div className="h-20 bg-muted rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Movimenti Bancari */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Banknote className="h-4 w-4 text-emerald-600" />
                Movimenti Bancari
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-2xl font-bold">{stats.movimenti_bancari.allocati}</p>
                  <p className="text-xs text-muted-foreground">allocati</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-amber-600">{stats.movimenti_bancari.da_allocare}</p>
                  <p className="text-xs text-muted-foreground">da allocare</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{stats.movimenti_bancari.totale} totali</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Movimenti Carte */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-violet-600" />
                Movimenti Carte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-2xl font-bold">{stats.movimenti_carte.allocati}</p>
                  <p className="text-xs text-muted-foreground">allocati</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-amber-600">{stats.movimenti_carte.da_allocare}</p>
                  <p className="text-xs text-muted-foreground">da allocare</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{stats.movimenti_carte.totale} totali</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fatture */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />{t('invoices.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-2xl font-bold">{stats.fatture.pagate}</p>
                  <p className="text-xs text-muted-foreground">pagate</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-amber-600">{stats.fatture.da_pagare}</p>
                  <p className="text-xs text-muted-foreground">da pagare</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{formatCurrency(stats.fatture.importo_da_pagare)}</p>
                  <p className="text-xs text-muted-foreground">residuo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Allocations Summary */}
      {stats && (
        <Card className="bg-muted/30">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">Banca→Fattura</Badge>
                <span className="font-medium">{stats.allocazioni.banca_fattura}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-cyan-100 text-cyan-700">Carta→Fattura</Badge>
                <span className="font-medium">{stats.allocazioni.carta_fattura}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">{t('common.transferBank')}</Badge>
                <span className="font-medium">{stats.allocazioni.giroconto_banca}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-violet-100 text-violet-700">{t('common.transferCard')}</Badge>
                <span className="font-medium">{stats.allocazioni.giroconto_carta}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Banca→Conto</Badge>
                <span className="font-medium">{stats.allocazioni.banca_conto}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Carta→Conto</Badge>
                <span className="font-medium">{stats.allocazioni.carta_conto}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtri:</span>
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-48" data-testid="filter-tipo">
                <SelectValue placeholder={t("common.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allTypes')}</SelectItem>
                <SelectItem value="banca_fattura">Banca → Fattura</SelectItem>
                <SelectItem value="carta_fattura">Carta → Fattura</SelectItem>
                <SelectItem value="giroconto">{t('common.transfers')}</SelectItem>
                <SelectItem value="giroconto_banca">{t('common.transferBank')}</SelectItem>
                <SelectItem value="giroconto_carta">{t('common.transferCard')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input 
                placeholder="Cerca descrizione o fornitore..." 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-64"
                data-testid="search-input"
              />
              <Button variant="outline" size="icon" onClick={handleSearch} data-testid="search-btn">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="ml-auto text-sm text-muted-foreground">
              {total} collegamenti totali
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="riconciliazioni-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 text-sm font-medium">{t("common.date")}</th>
                      <th className="text-left p-3 text-sm font-medium">{t("common.type")}</th>
                      <th className="text-left p-3 text-sm font-medium">{t('common.origin')}</th>
                      <th className="text-right p-3 text-sm font-medium">{t("common.amount")}</th>
                      <th className="text-center p-3 text-sm font-medium w-10"></th>
                      <th className="text-left p-3 text-sm font-medium">{t('common.destination')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center p-8 text-muted-foreground">
                          Nessun collegamento trovato
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-3 text-sm whitespace-nowrap">{item.data}</td>
                          <td className="p-3">{getTipoBadge(item.tipo_collegamento, item.origine_tipo)}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {getOriginIcon(item.origine_tipo)}
                              <span className="text-sm max-w-xs truncate" title={item.origine}>{item.origine}</span>
                            </div>
                          </td>
                          <td className="p-3 text-right font-medium whitespace-nowrap">
                            {formatCurrency(item.importo)}
                          </td>
                          <td className="p-3 text-center">
                            <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto" />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {item.tipo_collegamento === 'fattura' && <FileText className="h-4 w-4 text-blue-600" />}
                              {item.tipo_collegamento.startsWith('giroconto') && <RefreshCcw className="h-4 w-4 text-purple-600" />}
                              <span className="text-sm max-w-xs truncate" title={item.destinazione}>{item.destinazione}</span>
                              {item.destinazione_importo && (
                                <span className="text-xs text-muted-foreground">({formatCurrency(item.destinazione_importo)})</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Pagina {page} di {totalPages} ({total} totali)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t('common.previous')}</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.nextLabel')}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
